/**
 * Liste des devises supportées sur le site.
 * Utilisée pour : devise par défaut utilisateur, bankrolls, dépenses, tournois, conversions.
 */

export interface CurrencyItem {
  code: string;
  symbol: string;
  name: string;
}

const CURRENCY_CODE_ALIASES: Record<string, string> = {
  SFR: 'CHF',
};

/** Codes prioritaires affichés en tête dans les listes de devises. */
export const PRIORITY_CURRENCY_CODES = ['EUR', 'USD', 'GBP'] as const;

/** Toutes les devises supportées (EUR/USD/GBP en tête, puis ordre alphabétique). */
export const SUPPORTED_CURRENCIES: CurrencyItem[] = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'Pound Sterling' },
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Yuan Renminbi' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'HKD', symbol: '$', name: 'Hong Kong Dollar' },
  { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna' },
  { code: 'HUF', symbol: 'Ft', name: 'Forint' },
  { code: 'IDR', symbol: 'Rp', name: 'Rupiah' },
  { code: 'ILS', symbol: '₪', name: 'New Israeli Sheqel' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'ISK', symbol: 'kr', name: 'Iceland Krona' },
  { code: 'JPY', symbol: '¥', name: 'Yen' },
  { code: 'KRW', symbol: '₩', name: 'Won' },
  { code: 'MAD', symbol: 'MAD', name: 'Moroccan Dirham' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'NZD', symbol: '$', name: 'New Zealand Dollar' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'PLN', symbol: 'zł', name: 'Zloty' },
  { code: 'RON', symbol: 'lei', name: 'New Romanian Leu' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'SGD', symbol: '$', name: 'Singapore Dollar' },
  { code: 'THB', symbol: '฿', name: 'Baht' },
  { code: 'TRY', symbol: '₺', name: 'New Turkish Lira' },
  { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar' },
  { code: 'VND', symbol: '₫', name: 'Dong' },
  { code: 'ZAR', symbol: 'R', name: 'Rand' },
];

/** Set des codes pour validation rapide */
export const SUPPORTED_CURRENCY_CODES = new Set(SUPPORTED_CURRENCIES.map((c) => c.code));

/** Map code → symbole pour affichage */
export const CURRENCY_SYMBOL: Record<string, string> = Object.fromEntries(
  SUPPORTED_CURRENCIES.map((c) => [c.code, c.symbol])
);

/** Map code → item complet */
export const CURRENCY_BY_CODE: Record<string, CurrencyItem> = Object.fromEntries(
  SUPPORTED_CURRENCIES.map((c) => [c.code, c])
);

export function getCurrencyByCode(code: string): CurrencyItem | undefined {
  return CURRENCY_BY_CODE[normalizeCurrencyCode(code)];
}

export function getCurrencySymbol(code: string): string {
  const normalizedCode = normalizeCurrencyCode(code);
  return CURRENCY_SYMBOL[normalizedCode] ?? normalizedCode;
}

export function isSupportedCurrency(code: string): boolean {
  return SUPPORTED_CURRENCY_CODES.has(normalizeCurrencyCode(code));
}

export function normalizeCurrencyCode(code: string | null | undefined): string {
  const normalized = (code ?? '').trim().toUpperCase();
  if (!normalized) return '';
  return CURRENCY_CODE_ALIASES[normalized] ?? normalized;
}

export function normalizeCurrencyMentions(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/\bSFr\b/gi, 'CHF');
}

/** Devise par défaut si non renseignée (affichage, fallback) */
export const DEFAULT_CURRENCY = 'EUR';
